import React, { useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { logout } from '../../../slices/authSlice';
import isoLanguages from '../../isoLanguages';
import '../../DashboardHome/settings.css';
import '../Settings/settings.css';

const ICN = {
    user: (
        <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
            <circle cx="8" cy="5.5" r="2.6" stroke="currentColor" strokeWidth="1.5" />
            <path d="M3 13.5c.7-2.4 2.6-3.6 5-3.6s4.3 1.2 5 3.6" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
        </svg>
    ),
    bell: (
        <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
            <path d="M4 11V7.5a4 4 0 018 0V11l1 1.5H3L4 11z" stroke="currentColor" strokeWidth="1.5" strokeLinejoin="round" />
        </svg>
    ),
    card: (
        <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
            <rect x="2" y="3.5" width="12" height="9" rx="1.5" stroke="currentColor" strokeWidth="1.5" />
            <path d="M2 6.5h12" stroke="currentColor" strokeWidth="1.5" />
        </svg>
    ),
    download: (
        <svg width="13" height="13" viewBox="0 0 14 14" fill="none">
            <path d="M7 2v7m0 0l-3-3m3 3l3-3M2 11v1h10v-1" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
    ),
};

const SECTIONS = [
    { id: 'profile', label: 'Profile', icon: ICN.user, group: 'Account' },
    { id: 'notifications', label: 'Notifications', icon: ICN.bell, group: 'Account' },
    { id: 'billing', label: 'Billing & plan', icon: ICN.card, group: 'Account' },
];

const NOTIF_ROWS = [
    ['assigned', 'Project assigned to you', 'When you’re added as an owner or reviewer.'],
    ['mentions', 'Comments & mentions', 'Replies and @-mentions on your segments.'],
    ['approvals', 'Approvals needed', 'A file is waiting on your sign-off.'],
    ['deadlines', 'Deadline reminders', '24 hours before a project is due.'],
    ['conflicts', 'Glossary conflicts', 'A term you own is mapped two different ways.'],
    ['completed', 'Project completed', 'Every language in a project reaches 100%.'],
];

const INTERFACE_LANGS = [
    { value: 'en', label: 'English' },
    { value: 'de', label: 'Deutsch' },
    { value: 'fr', label: 'Français' },
    { value: 'es', label: 'Español' },
    { value: 'ja', label: '日本語' },
];

const deepSet = (obj, path, value) => {
    const parts = path.split('.');
    const next = { ...obj };
    let cur = next;
    for (let i = 0; i < parts.length - 1; i += 1) {
        cur[parts[i]] = { ...(cur[parts[i]] || {}) };
        cur = cur[parts[i]];
    }
    cur[parts[parts.length - 1]] = value;
    return next;
};

// ============ Primitives ============
const SetBlock = ({ title, desc, flush, children }) => (
    <div className="set-block">
        <div className={`set-block-head ${flush ? 'flush' : ''}`}>
            <h3>{title}</h3>
            {desc && <p>{desc}</p>}
        </div>
        {children}
    </div>
);

const SetField = ({ label, desc, children }) => (
    <div className="set-field">
        <div className="set-field-label">
            <div className="lbl">{label}</div>
            {desc && <div className="desc">{desc}</div>}
        </div>
        <div className="set-field-control">{children}</div>
    </div>
);

const SetText = ({ value, onChange, type = 'text', placeholder }) => (
    <input
        className="set-input"
        type={type}
        value={value || ''}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
    />
);

const SetSelect = ({ value, onChange, options, minWidth }) => (
    <select
        className="set-input"
        value={value || ''}
        onChange={(e) => onChange(e.target.value)}
        style={minWidth ? { minWidth } : undefined}
    >
        {options.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
    </select>
);

const SetSegmented = ({ value, onChange, options }) => (
    <div className="bv-seg">
        {options.map((o) => (
            <button
                type="button"
                key={o.value}
                className={value === o.value ? 'on' : ''}
                onClick={() => onChange(o.value)}
            >
                {o.label}
            </button>
        ))}
    </div>
);

const SetToggle = ({ checked, onChange }) => (
    <button
        type="button"
        onClick={() => onChange(!checked)}
        aria-pressed={checked}
        style={{
            width: 38, height: 22, borderRadius: 999,
            border: 0,
            background: checked ? 'var(--accent)' : 'var(--bg-soft)',
            position: 'relative', cursor: 'pointer',
        }}
    >
        <span style={{
            position: 'absolute', top: 2,
            left: checked ? 18 : 2,
            width: 18, height: 18,
            background: 'white', borderRadius: 999,
            transition: 'left 0.15s',
            boxShadow: '0 1px 2px rgba(0,0,0,0.2)',
        }} />
    </button>
);

// ============ Profile section ============
const ProfileSection = ({ s, set, accountType, onPickAvatar, onRemoveAvatar }) => {
    const initials = `${(s.profile.firstName || '').charAt(0)}${(s.profile.lastName || '').charAt(0)}`.toUpperCase()
        || (s.profile.email || '?').slice(0, 2).toUpperCase();
    // Mirror the gating from the legacy ProfilePage: free → tier 1 only,
    // essential → tiers 1–2, paid plans → all languages.
    const languageOptions = [{ value: '', label: '— Select a language —' }].concat(
        isoLanguages
            .filter((lang) => {
                if (accountType === 'free') return lang.tier <= 1;
                if (accountType === 'essential') return lang.tier <= 2;
                return true;
            })
            .map((lang) => ({
                value: lang.code.toLowerCase(),
                label: `${lang.name} (${lang.code.toUpperCase()})`,
            })),
    );
    return (
        <>
            <div className="set-section-head">
                <h2>Your profile</h2>
                <p>How you appear to teammates. These details are personal to your account.</p>
            </div>

            <SetBlock title="Identity">
                <div className="set-logo-row">
                    {s.profile.avatarUrl ? (
                        <img
                            src={s.profile.avatarUrl}
                            alt="avatar"
                            className="set-logo-art"
                            style={{ objectFit: 'cover' }}
                        />
                    ) : (
                        <div className="set-logo-art">{initials || 'MK'}</div>
                    )}
                    <div className="set-logo-meta">
                        <b>Profile photo</b>
                        <span>PNG or JPG, at least 128×128px</span>
                    </div>
                    <div className="set-logo-actions">
                        <button type="button" className="set-btn sm" onClick={onPickAvatar}>Upload</button>
                        <button type="button" className="set-btn sm" onClick={onRemoveAvatar}>Remove</button>
                    </div>
                </div>
                <SetField label="First name">
                    <SetText value={s.profile.firstName} onChange={(v) => set('profile.firstName', v)} />
                </SetField>
                <SetField label="Last name">
                    <SetText value={s.profile.lastName} onChange={(v) => set('profile.lastName', v)} />
                </SetField>
                <SetField label="Job title" desc="Shown on your profile and in activity feeds.">
                    <SetText value={s.profile.jobTitle} onChange={(v) => set('profile.jobTitle', v)} />
                </SetField>
                <SetField label="Company" desc="The company or agency you work for.">
                    <SetText value={s.profile.company} onChange={(v) => set('profile.company', v)} />
                </SetField>
            </SetBlock>

            <SetBlock title="Work" desc="Used by owners when routing projects and auto-assigning languages.">
                <SetField label="Translated language" desc="The language you translate into. Owners use this to route work to you.">
                    <SetSelect
                        value={s.profile.translatedLanguage}
                        onChange={(v) => set('profile.translatedLanguage', (v || '').toLowerCase())}
                        options={languageOptions}
                        minWidth={220}
                    />
                </SetField>
                <SetField label="Availability" desc="When you can typically take on new work.">
                    <SetText
                        value={s.profile.availability}
                        onChange={(v) => set('profile.availability', v)}
                        placeholder="e.g. Weekdays, GMT business hours"
                    />
                </SetField>
                <SetField label="Workload / capacity" desc="Rough indication of how much work you can take on.">
                    <SetText
                        value={s.profile.workloadCapacity}
                        onChange={(v) => set('profile.workloadCapacity', v)}
                        placeholder="e.g. 20 hrs/week"
                    />
                </SetField>
                <SetField label="Domains / expertise" desc="Subject areas you specialise in. Separate multiple with commas.">
                    <SetText
                        value={s.profile.domains}
                        onChange={(v) => set('profile.domains', v)}
                        placeholder="e.g. Medical, Legal, Technical"
                    />
                </SetField>
            </SetBlock>

            <SetBlock title="Socials" desc="Optional links shown on your profile.">
                <SetField label="LinkedIn">
                    <SetText
                        value={s.profile.linkedInProfile}
                        onChange={(v) => set('profile.linkedInProfile', v)}
                        placeholder="https://www.linkedin.com/in/…"
                    />
                </SetField>
                <SetField label="Website / portfolio">
                    <SetText
                        value={s.profile.website}
                        onChange={(v) => set('profile.website', v)}
                        placeholder="https://…"
                    />
                </SetField>
                <SetField label="Twitter / X">
                    <SetText
                        value={s.profile.twitter}
                        onChange={(v) => set('profile.twitter', v)}
                        placeholder="https://twitter.com/…"
                    />
                </SetField>
                <SetField label="GitHub">
                    <SetText
                        value={s.profile.github}
                        onChange={(v) => set('profile.github', v)}
                        placeholder="https://github.com/…"
                    />
                </SetField>
                <SetField label="Instagram">
                    <SetText
                        value={s.profile.instagram}
                        onChange={(v) => set('profile.instagram', v)}
                        placeholder="https://instagram.com/…"
                    />
                </SetField>
            </SetBlock>

            <SetBlock title="Contact & sign-in">
                <SetField label="Email address" desc="Used for sign-in and account notifications.">
                    <SetText type="email" value={s.profile.email} onChange={() => {}} />
                </SetField>
                <SetField label="Password" desc="Last changed: unknown.">
                    <button type="button" className="set-btn sm">Change password</button>
                </SetField>
            </SetBlock>

            <SetBlock title="Preferences" desc="Personal interface settings — these only affect your account.">
                <SetField label="Interface language" desc="The language the Rush Studio UI is shown in.">
                    <SetSelect
                        value={s.profile.interfaceLang}
                        onChange={(v) => set('profile.interfaceLang', v)}
                        options={INTERFACE_LANGS}
                        minWidth={160}
                    />
                </SetField>
                <SetField label="Appearance" desc="Match your system, or pick a fixed theme.">
                    <SetSegmented
                        value={s.profile.theme}
                        onChange={(v) => set('profile.theme', v)}
                        options={[
                            { value: 'system', label: 'System' },
                            { value: 'light', label: 'Light' },
                            { value: 'dark', label: 'Dark' },
                        ]}
                    />
                </SetField>
            </SetBlock>
        </>
    );
};

// ============ Notifications section ============
const NotificationsSection = ({ s, set }) => (
    <>
        <div className="set-section-head">
            <h2>Notifications</h2>
            <p>Choose what reaches you by email and in-app. These preferences are personal to your account.</p>
        </div>

        <SetBlock title="Activity" flush>
            <div className="set-notif-head">
                <span>Notify me about</span>
                <span className="col-c">Email</span>
                <span className="col-c">In-app</span>
            </div>
            {NOTIF_ROWS.map(([key, label, desc]) => {
                const row = (s.notif && s.notif[key]) || { email: true, inapp: true };
                return (
                    <div className="set-notif-row" key={key}>
                        <div>
                            <div className="nlbl">{label}</div>
                            <div className="ndesc">{desc}</div>
                        </div>
                        <div className="col-c">
                            <SetToggle checked={!!row.email} onChange={(v) => set(`notif.${key}.email`, v)} />
                        </div>
                        <div className="col-c">
                            <SetToggle checked={!!row.inapp} onChange={(v) => set(`notif.${key}.inapp`, v)} />
                        </div>
                    </div>
                );
            })}
        </SetBlock>

        <SetBlock title="Digest & timing">
            <SetField label="Weekly summary" desc="A Monday-morning digest of workspace activity.">
                <SetSegmented
                    value={(s.notif && s.notif.digest) || 'on'}
                    onChange={(v) => set('notif.digest', v)}
                    options={[
                        { value: 'on', label: 'On' },
                        { value: 'off', label: 'Off' },
                    ]}
                />
            </SetField>
            <SetField label="Quiet hours" desc="Pause email notifications overnight in your time zone.">
                <SetToggle checked={!!(s.notif && s.notif.quietHours)} onChange={(v) => set('notif.quietHours', v)} />
            </SetField>
        </SetBlock>
    </>
);

// ============ Billing section ============
// Inline AI-credit purchase. Lazily loads the configured packs, then sends the
// buyer to a Stripe Checkout session; the webhook grants the words on payment.
// Returns the user to the current page on success/cancel.
const CreditPacks = ({ token, open, setOpen }) => {
    const [packs, setPacks] = useState(null);
    const [loading, setLoading] = useState(false);
    const [busyKey, setBusyKey] = useState(null);
    const [err, setErr] = useState(null);

    // Load packs the first time the panel is opened (from the button or a
    // ≥80% "Top up" link / ?buy=credits deep link — all just flip `open`).
    useEffect(() => {
        if (open && packs == null) {
            setLoading(true); setErr(null);
            axios.get('/api/billing/credits/packs', { headers: { Authorization: `Bearer ${token}` } })
                .then((res) => setPacks(res.data.packs || []))
                .catch(() => setErr('Could not load credit packs.'))
                .finally(() => setLoading(false));
        }
    }, [open, packs, token]);

    const buy = async (key) => {
        setBusyKey(key); setErr(null);
        try {
            const here = window.location.href;
            const res = await axios.post(
                '/api/billing/credits/checkout',
                { packKey: key, successUrl: here, cancelUrl: here },
                { headers: { Authorization: `Bearer ${token}` } }
            );
            if (res.data?.url) window.location.assign(res.data.url);
            else { setErr('Could not start checkout.'); setBusyKey(null); }
        } catch (e) {
            setErr(e.response?.data?.message || 'Could not start checkout.');
            setBusyKey(null);
        }
    };

    return (
        <div className="u" style={{ gridColumn: '1 / -1' }}>
            <button type="button" className="set-btn" onClick={() => setOpen(!open)}>
                {open ? 'Hide credit packs' : 'Buy AI credits'}
            </button>
            {open && (
                <div style={{ marginTop: 10, display: 'flex', gap: 10, flexWrap: 'wrap' }}>
                    {loading && <span style={{ fontSize: 13, color: 'var(--ink-3)' }}>Loading…</span>}
                    {!loading && packs && packs.length === 0 && (
                        <span style={{ fontSize: 13, color: 'var(--ink-3)' }}>Credit packs aren&rsquo;t available yet.</span>
                    )}
                    {!loading && packs && packs.map((p) => (
                        <button
                            key={p.key}
                            type="button"
                            className="set-btn"
                            disabled={!!busyKey}
                            onClick={() => buy(p.key)}
                            style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', gap: 2, minWidth: 132 }}
                        >
                            <b>{(p.words / 1000).toFixed(0)}k AI words</b>
                            <span style={{ fontSize: 12, opacity: 0.8 }}>£{p.gbp} · £{p.perThousand}/1k</span>
                            {busyKey === p.key && <span style={{ fontSize: 11 }}>Redirecting…</span>}
                        </button>
                    ))}
                    {err && <span style={{ color: '#c33', fontSize: 12, width: '100%' }}>{err}</span>}
                </div>
            )}
        </div>
    );
};

const BillingSection = ({ s, navigate, token, isOwner }) => {
    const b = s.billing;
    // Lifted so both the "Buy AI credits" button and the ≥80% "Top up" link on
    // the AI-words bar (and the ?buy=credits deep link) drive the same panel.
    const [creditsOpen, setCreditsOpen] = useState(false);
    useEffect(() => {
        if (typeof window !== 'undefined' && window.location.search.includes('buy=credits')) {
            setCreditsOpen(true);
        }
    }, []);
    const planLabel = b.planLabel || (b.plan || 'essential').replace(/^\w/, (c) => c.toUpperCase());

    // Render a seat tile that handles both bounded ("3 / 25") and unbounded
    // ("12 / ∞") states. Bar is hidden in the unbounded case — there's
    // nothing meaningful to fill.
    const SeatTile = ({ label, used, limit, unlimited }) => {
        const pct = unlimited || limit === 0 ? 0 : Math.min(100, (used / limit) * 100);
        const available = Math.max(0, (limit || 0) - used);
        return (
            <div className="u">
                <div className="ul">{label}</div>
                <div className="uv">
                    {used}<span className="unit">/ {unlimited ? '∞' : limit}</span>
                </div>
                <div className="ubar">
                    {!unlimited && <span style={{ width: `${pct}%` }} />}
                </div>
                <div className="us">
                    {unlimited
                        ? 'No seat limit on this plan'
                        : `${available} seat${available === 1 ? '' : 's'} available`}
                </div>
            </div>
        );
    };

    // Word tiles — an Infinity limit renders "unlimited"; a 0 limit means the
    // allowance isn't included on this plan (e.g. Free has no AI words).
    const wordStat = (used, limit) => {
        const unlimited = !Number.isFinite(limit);
        const none = limit === 0;
        const pct = unlimited || none ? 0 : Math.min(100, (used / limit) * 100);
        return { unlimited, none, pct };
    };
    const twStat = wordStat(b.translatedWordsThisMonth, b.translatedWordsLimit);
    const aiStat = wordStat(b.aiWordsThisMonth, b.aiWordsLimit);
    const tmUnlimited = !Number.isFinite(b.tmStorageLimitGb);
    const tmPct = tmUnlimited ? 0 : Math.min(100, (b.tmStorageGb / b.tmStorageLimitGb) * 100);

    return (
        <>
            <div className="set-section-head">
                <h2>Billing &amp; plan</h2>
                <p>Manage your subscription, seats, and usage. Invoices are sent to your billing contact each month.</p>
            </div>

            <SetBlock title="Current plan">
                <div className="set-plan">
                    <div>
                        <span className="badge">Current plan</span>
                        <div className="pn" style={{ marginTop: 8 }}>{planLabel}</div>
                        <div className="pp">
                            {b.cost == null
                                ? 'Contact sales for pricing'
                                : b.cost > 0
                                    ? <><b>£{b.cost}</b> / month</>
                                    : 'Free tier'}
                        </div>
                    </div>
                    <div className="grow" />
                    <button type="button" className="set-btn" onClick={() => navigate('/dashboard/pricing')}>Change plan</button>
                    <button type="button" className="set-btn accent" onClick={() => navigate('/dashboard/pricing')}>Upgrade</button>
                </div>
                <div className="set-usage">
                    <SeatTile
                        label="Manager seats"
                        used={b.fullSeats}
                        limit={b.fullSeatLimit}
                        unlimited={b.fullSeatUnlimited}
                    />
                    <SeatTile
                        label="Linguist seats"
                        used={b.linguistSeats}
                        limit={b.linguistSeatLimit}
                        unlimited={b.linguistSeatUnlimited}
                    />
                    <div className="u">
                        <div className="ul">Translated words this month</div>
                        <div className="uv">
                            {(b.translatedWordsThisMonth / 1000).toFixed(0)}k
                            <span className="unit">/ {twStat.unlimited ? '∞' : `${(b.translatedWordsLimit / 1000).toFixed(0)}k`}</span>
                        </div>
                        <div className="ubar">
                            {!twStat.unlimited && !twStat.none && <span className={twStat.pct > 80 ? 'warn' : ''} style={{ width: `${twStat.pct}%` }} />}
                        </div>
                        <div className="us">
                            {twStat.unlimited
                                ? 'Unlimited on this plan'
                                : twStat.none
                                    ? 'Not included on this plan'
                                    : `${Math.round(twStat.pct)}% of monthly allowance`}
                        </div>
                    </div>
                    <div className="u">
                        <div className="ul">AI words this month</div>
                        <div className="uv">
                            {(b.aiWordsThisMonth / 1000).toFixed(0)}k
                            <span className="unit">/ {aiStat.unlimited ? '∞' : `${(b.aiWordsLimit / 1000).toFixed(0)}k`}</span>
                        </div>
                        <div className="ubar">
                            {!aiStat.unlimited && !aiStat.none && <span className={aiStat.pct > 80 ? 'warn' : ''} style={{ width: `${aiStat.pct}%` }} />}
                        </div>
                        <div className="us">
                            {aiStat.unlimited
                                ? 'Unlimited on this plan'
                                : aiStat.none
                                    ? 'Not included on this plan'
                                    : `${Math.round(aiStat.pct)}% of monthly allowance`}
                            {b.aiCreditsBalance > 0 && (
                                <> · <b>{(b.aiCreditsBalance / 1000).toFixed(b.aiCreditsBalance < 1000 ? 1 : 0)}k</b> credits remaining</>
                            )}
                            {isOwner && !aiStat.unlimited && !aiStat.none && aiStat.pct >= 80 && (
                                <> · <button
                                    type="button"
                                    onClick={() => setCreditsOpen(true)}
                                    style={{ background: 'transparent', border: 0, padding: 0, color: 'var(--accent, #3b6fe0)', font: 'inherit', fontWeight: 600, cursor: 'pointer', textDecoration: 'underline' }}
                                >
                                    Top up
                                </button></>
                            )}
                        </div>
                    </div>
                    <div className="u">
                        <div className="ul">TM storage</div>
                        <div className="uv">
                            {b.tmStorageGb.toFixed(1)}<span className="unit">/ {tmUnlimited ? '∞' : `${b.tmStorageLimitGb} GB`}</span>
                        </div>
                        <div className="ubar">
                            {!tmUnlimited && <span style={{ width: `${tmPct}%` }} />}
                        </div>
                        <div className="us">{(b.tmSegments || 0).toLocaleString()} segments stored</div>
                    </div>
                    {isOwner && <CreditPacks token={token} open={creditsOpen} setOpen={setCreditsOpen} />}
                </div>
            </SetBlock>

            <SetBlock title="Payment method">
                <div className="set-list-row">
                    <div className="set-avatar">{ICN.card}</div>
                    <div className="li-main">
                        <b>{s.billing.cardLabel || 'No payment method on file'}</b>
                        {s.billing.cardLabel && <span>{s.billing.cardSubtitle}</span>}
                    </div>
                    <div className="grow" />
                    <button type="button" className="set-btn sm">Update</button>
                </div>
            </SetBlock>

            <SetBlock title="Invoices" flush>
                {(s.billing.invoices || []).length === 0 && (
                    <div style={{ padding: 16, color: 'var(--ink-4)', fontSize: 13 }}>
                        No invoices yet.
                    </div>
                )}
                {(s.billing.invoices || []).map((inv) => (
                    <div className="set-list-row" key={inv.number}>
                        <div className="li-main">
                            <b>{inv.date}</b>
                            <span className="mono">{inv.number}</span>
                        </div>
                        <div className="grow" />
                        <span className="li-amt">{inv.amount}</span>
                        <span className="set-pill green"><span className="pdot" />{inv.status}</span>
                        <button type="button" className="set-btn sm">{ICN.download}</button>
                    </div>
                ))}
            </SetBlock>
        </>
    );
};

// ============ Main ============
const Account = () => {
    const token = useSelector((s) => s.auth.token);
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const fileInputRef = useRef(null);

    const [user, setUser] = useState(null);
    const [state, setState] = useState(null);
    const [active, setActive] = useState(() => (window.location.hash || '#profile').replace('#', ''));
    const [dirty, setDirty] = useState(false);
    const [saving, setSaving] = useState(false);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    useEffect(() => {
        if (!token) return;
        const headers = { Authorization: `Bearer ${token}` };
        // /api/plans is the source of truth for prices and per-plan limits.
        // /api/team/limits gives live seat usage. Both fail-soft so a transient
        // 500 on either doesn't break the rest of the page.
        Promise.all([
            axios.get('/api/users/me', { headers }),
            axios.get('/api/team/limits', { headers }).catch(() => ({ data: null })),
            axios.get('/api/plans').catch(() => ({ data: { plans: [] } })),
        ])
            .then(([meRes, limitsRes, plansRes]) => {
                const r = meRes;
                const limits = limitsRes.data || null;
                setUser(r.data);
                const planKey = (r.data.accountType || 'essential').toLowerCase();

                // Pull this user's plan from the catalogue. The endpoint
                // returns null for unlimited — we translate that to Infinity
                // here so the existing render code (which expects numbers)
                // doesn't need to learn about a new sentinel.
                const catalogue = plansRes.data?.plans || [];
                const currentPlan = catalogue.find((p) => p.key === planKey);
                const orInf = (n) => (n == null ? Infinity : n);
                const aiWordsLimit = currentPlan ? orInf(currentPlan.limits.aiWords) : 0;
                const translatedWordsLimit = currentPlan ? orInf(currentPlan.limits.translatedWords) : 0;
                const tmStorageLimitGb = currentPlan ? orInf(currentPlan.limits.storageGb) : 0;
                const cost = currentPlan ? currentPlan.monthlyGbp : 0;

                // translatedLanguages is stored as an array on the User model
                // but the legacy ProfilePage only ever set a single value, so we
                // surface the first entry here and write back as one item.
                const tl = Array.isArray(r.data.translatedLanguages)
                    ? r.data.translatedLanguages[0]
                    : r.data.translatedLanguages;
                const domains = Array.isArray(r.data.domains)
                    ? r.data.domains.join(', ')
                    : (r.data.domains || '');

                setState({
                    profile: {
                        firstName: r.data.firstName || '',
                        lastName: r.data.lastName || '',
                        email: r.data.email || '',
                        jobTitle: r.data.occupation || '',
                        company: r.data.company || '',
                        avatarUrl: r.data.avatarUrl || '',
                        interfaceLang: r.data.interfaceLang || 'en',
                        theme: r.data.theme || 'system',
                        translatedLanguage: (tl || '').toLowerCase(),
                        availability: r.data.availability || '',
                        workloadCapacity: r.data.workloadCapacity || '',
                        domains,
                        linkedInProfile: r.data.linkedInProfile || '',
                        website: r.data.website || '',
                        twitter: r.data.twitter || '',
                        github: r.data.github || '',
                        instagram: r.data.instagram || '',
                    },
                    notif: r.data.notifPrefs || {
                        assigned: { email: true, inapp: true },
                        mentions: { email: true, inapp: true },
                        approvals: { email: true, inapp: true },
                        deadlines: { email: true, inapp: true },
                        conflicts: { email: false, inapp: true },
                        completed: { email: false, inapp: true },
                        digest: 'on',
                        quietHours: false,
                    },
                    billing: {
                        plan: planKey,
                        planLabel: limits?.planLabel || currentPlan?.label || planKey.charAt(0).toUpperCase() + planKey.slice(1),
                        cost,
                        contactSales: !!currentPlan?.contactSales,
                        currency: 'GBP',
                        fullSeats: limits?.fullSeats ?? 0,
                        fullSeatLimit: limits?.fullSeatLimit ?? 0,
                        fullSeatUnlimited: !!limits?.fullSeatLimitIsUnlimited,
                        linguistSeats: limits?.linguistSeats ?? 0,
                        linguistSeatLimit: limits?.linguistSeatLimit ?? 0,
                        linguistSeatUnlimited: !!limits?.linguistSeatLimitIsUnlimited,
                        aiWordsThisMonth: r.data.deeplUsage || 0,
                        aiWordsLimit,
                        aiCreditsBalance: r.data.aiCreditsBalance || 0,
                        translatedWordsThisMonth: r.data.translatedWordsUsage || 0,
                        translatedWordsLimit,
                        tmStorageGb: ((r.data.storageUsed || 0) / (1024 * 1024 * 1024)),
                        tmStorageLimitGb,
                        tmSegments: r.data.tmSegmentsCount || 0,
                        cardLabel: r.data.stripeCustomerId ? 'Card on file' : 'No payment method on file',
                        cardSubtitle: r.data.stripeCustomerId ? 'Managed via Stripe' : '',
                        invoices: [],
                    },
                });
                setLoading(false);
            })
            .catch((err) => {
                if (err.response && err.response.status === 401) {
                    dispatch(logout());
                    navigate('/login');
                } else {
                    setError('Failed to load account.');
                    setLoading(false);
                }
            });
    }, [token, dispatch, navigate]);

    useEffect(() => {
        const onHash = () => setActive((window.location.hash || '#profile').replace('#', ''));
        window.addEventListener('hashchange', onHash);
        return () => window.removeEventListener('hashchange', onHash);
    }, []);

    const set = (path, value) => {
        setState((prev) => deepSet(prev, path, value));
        setDirty(true);
    };

    const save = async () => {
        if (!state) return;
        setSaving(true);
        try {
            // Server accepts translatedLanguages as a string or array and
            // wraps a single value into an array on write — we always send
            // one value here to match the single-select UI.
            await axios.put('/api/users/me', {
                firstName: state.profile.firstName,
                lastName: state.profile.lastName,
                occupation: state.profile.jobTitle,
                company: state.profile.company,
                interfaceLang: state.profile.interfaceLang,
                theme: state.profile.theme,
                translatedLanguages: state.profile.translatedLanguage || '',
                availability: state.profile.availability,
                workloadCapacity: state.profile.workloadCapacity,
                domains: state.profile.domains,
                linkedInProfile: state.profile.linkedInProfile,
                website: state.profile.website,
                twitter: state.profile.twitter,
                github: state.profile.github,
                instagram: state.profile.instagram,
                notifPrefs: state.notif,
            }, { headers: { Authorization: `Bearer ${token}` } });
            setDirty(false);
        } catch (e) {
            setError('Save failed.');
        } finally {
            setSaving(false);
        }
    };

    const onPickAvatar = () => {
        if (fileInputRef.current) fileInputRef.current.click();
    };

    const onAvatarChosen = async (e) => {
        const file = e.target.files && e.target.files[0];
        if (!file) return;
        const form = new FormData();
        form.append('avatar', file);
        try {
            const r = await axios.put('/api/users/me', form, {
                headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'multipart/form-data' },
            });
            const url = r.data?.user?.avatarUrl;
            if (url) set('profile.avatarUrl', url);
        } catch (e2) {
            setError('Avatar upload failed.');
        }
    };

    const onRemoveAvatar = () => set('profile.avatarUrl', '');

    const selectSection = (id) => {
        setActive(id);
        try { window.history.replaceState(null, '', `#${id}`); } catch { window.location.hash = id; }
    };

    if (loading) return <div style={{ padding: 40, color: 'var(--ink-3)' }}>Loading account…</div>;
    if (error) return <div style={{ padding: 40, color: 'crimson' }}>{error}</div>;
    if (!state) return null;

    return (
        <>
            <div className="rs-page-title-row">
                <h1 className="rs-page-title">Your account</h1>
            </div>
            <p className="rs-page-sub">Personal profile, notifications, and billing.</p>

            <div className="set-layout">
                <aside className="set-rail">
                    <div className="set-rail-group">
                        <div style={{ fontSize: 10.5, fontWeight: 700, color: 'var(--ink-4)', letterSpacing: '0.06em', textTransform: 'uppercase', padding: '4px 12px 6px' }}>
                            Account
                        </div>
                        {SECTIONS.map((sec) => (
                            <button
                                key={sec.id}
                                type="button"
                                className={`set-rail-item ${active === sec.id ? 'on' : ''}`}
                                onClick={() => selectSection(sec.id)}
                            >
                                <span className="ic">{sec.icon}</span>
                                <span>{sec.label}</span>
                            </button>
                        ))}
                    </div>
                </aside>

                <div className="set-body">
                    {active === 'profile' && (
                        <ProfileSection
                            s={state}
                            set={set}
                            accountType={(user && user.accountType) || 'free'}
                            onPickAvatar={onPickAvatar}
                            onRemoveAvatar={onRemoveAvatar}
                        />
                    )}
                    {active === 'notifications' && <NotificationsSection s={state} set={set} />}
                    {active === 'billing' && <BillingSection s={state} navigate={navigate} token={token} isOwner={!!user?.isBillingOwner} />}
                </div>
            </div>

            <input
                ref={fileInputRef}
                type="file"
                accept="image/*"
                style={{ display: 'none' }}
                onChange={onAvatarChosen}
            />

            {dirty && (
                <div style={{
                    position: 'sticky', bottom: 16, marginTop: 16,
                    background: 'var(--bg-card)', border: '1px solid var(--line)',
                    borderRadius: 12, padding: 12, display: 'flex',
                    alignItems: 'center', gap: 12, boxShadow: 'var(--shadow-md)',
                }}>
                    <span style={{ flex: 1, fontSize: 13, color: 'var(--ink-3)' }}>You have unsaved changes.</span>
                    <button type="button" className="btn-q" onClick={() => window.location.reload()}>Discard</button>
                    <button type="button" className="btn-acc" onClick={save} disabled={saving}>
                        {saving ? 'Saving…' : 'Save changes'}
                    </button>
                </div>
            )}
        </>
    );
};

export default Account;
